import React from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { CheckmarkIcon } from '@cp/ds/src/icons/checkmark';
import { Typography } from '@cp/ds/src/components/typography';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme/const';
import { cx } from '@emotion/css';
import { ClientType } from '@common/model/client/clientType';
import { CLIENT_TYPE_TO_CLIENT_TYPE_NAME, EButtonName, EPageName, useTrackMount, withTrackClick } from 'src/shared/tracking';
import { StepFooter } from '../stepFooter';
import { afterAllShow, createResumeClick } from '../../lib';
import { ClientRegistrationStepProps } from './model';

const SubmitButton = withTrackClick(Button, createResumeClick);

type ItemProps = { number: number; checked?: boolean; time: string; mainText: React.ReactNode; secondaryText?: React.ReactNode };
const Item = ({ number, checked, time, mainText, secondaryText }: ItemProps) => {
  const theme = useTheme();
  return (
    <Box component="li" sx={{ display: 'flex', alignItems: 'flex-start' }}>
      <Box
        sx={{
          ...theme.typography.button,
          marginRight: 2.5,
          flex: '0 0 60px',
          borderRadius: '50%',
          width: '60px',
          height: '60px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: checked ? theme.palette.green : theme.palette.white,
          color: checked ? theme.palette.white : theme.palette.gray,
          boxShadow: '0px 2px 3px rgba(0, 0, 0, 0.10)',
        }}
      >
        {checked ? <CheckmarkIcon /> : number}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', [MOBILE_BREAKPOINT_THRESHOLD]: 'row' },
          columnGap: { xs: 0, [MOBILE_BREAKPOINT_THRESHOLD]: 2.5 },
        }}
      >
        <Typography component="div" sx={{ flex: '1 0 60%' }} variant="h3">
          {mainText}
        </Typography>
        <Typography
          component="div"
          sx={{
            flex: '1 0 30%',
            color: checked ? theme.palette.green : theme.palette.gray,
            textAlign: { xs: 'left', [MOBILE_BREAKPOINT_THRESHOLD]: 'right' },
            order: { xs: 3, [MOBILE_BREAKPOINT_THRESHOLD]: 0 },
          }}
          variant="caption"
        >
          {time}
        </Typography>
        <Typography component="div" sx={{ flex: '1 0 100%', color: theme.palette.gray }} variant="body bold">
          {secondaryText}
        </Typography>
      </Box>
    </Box>
  );
};

export const StepAfterAll: React.FC<ClientRegistrationStepProps> = ({ onSubmit, model, state }) => {
  useTrackMount(afterAllShow);
  return (
    <Box
      sx={{
        maxWidth: '500px',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        gap: 5,
        pt: 2.5,
      }}
    >
      <Typography sx={{ textAlign: 'center' }} variant="h1">
        Отличное начало!
        <br /> Вот, что будет дальше
      </Typography>
      <Box
        component="ol"
        sx={{
          display: 'flex',
          gap: 4.5,
          padding: 0,
          mx: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 'auto' },
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <Item checked mainText="Создайте учетную запись" number={1} secondaryText="" time="Готово!" />
        <Item
          mainText="Создайте свою анкету"
          number={2}
          secondaryText="Установите приложение, загрузите фото, заполните информацию о себе"
          time="Займет 4 минуты"
        />
        <Item
          mainText="Пройдите проверку"
          number={3}
          secondaryText="Ответьте на несколько вопросов безопасности и получите значок “Проверен” для вашего профиля"
          time="Займет еще пару минут"
        />
      </Box>
      <StepFooter>
        <SubmitButton
          className={cx(
            EButtonName.Next,
            EPageName.Registration,
            model.name,
            CLIENT_TYPE_TO_CLIENT_TYPE_NAME[state.clientType as ClientType],
          )}
          onClick={() => onSubmit({})}
          sx={{
            width: { xs: '100%', [MOBILE_BREAKPOINT_THRESHOLD]: 'auto' },
          }}
        >
          Создать анкету
        </SubmitButton>
      </StepFooter>
    </Box>
  );
};
